<script lang="ts" setup>
  import { useVerifiedCart } from '@storefront/storefront';
  import { useAuthenticated } from '@othercommerce/foundation';

  interface Props {
    stage: number;
  }

  defineProps<Props>();

  const authenticated = useAuthenticated();
  const cart = useVerifiedCart();
</script>
<template>
  <main id="main">
    <div class="order">
      <div class="order-details">
        <div class="order-details__column order-details__column--left">
          <CartStage id="cs-cart" :current="stage" :stage="1" :title="$t('SHOP.CART.HEADER.CART')">
            <template #summary>
              <UiButtonGroup>
                <SfButton :href="$route('web.cart.show')" small>
                  <template #before>
                    <SfIconPen aria-hidden="true" />
                  </template>
                  <template #default>
                    {{ $t('SHOP.ACTION.EDIT') }}
                  </template>
                </SfButton>
              </UiButtonGroup>
            </template>
          </CartStage>
          <CartStage id="cs-account" :current="stage" :stage="2" :title="$t('SHOP.CART.HEADER.YOUR_ACCOUNT')">
            <template #summary>
              <template v-if="authenticated">
                <p>{{ $t('SHOP.CART.LABEL.PLACING_ORDER_AS') }} <b>{{ authenticated.name || authenticated.company }}</b></p>
              </template>
              <template v-else>
                <p>{{ $t('SHOP.CART.LABEL.PLACING_ORDER_AS') }} <b>{{ $t('SHOP.CART.LABEL.GUEST') }}</b></p>
                <AccountSignUpAdvantages />
                <UiButtonGroup>
                  <SfButton :href="$route('web.cart.account.show')" small>
                    {{ $t('SHOP.CART.ACTION.SIGN_IN_OR_REGISTER') }}
                  </SfButton>
                </UiButtonGroup>
              </template>
            </template>
          </CartStage>
          <CartStage id="cs-recipient" :current="stage" :stage="3" :title="$t('SHOP.CART.HEADER.SHIPPING_ADDRESS')">
            <template #default>
              <slot />
            </template>
            <template #summary>
              <address>
                <p v-if="cart.shippingName"><b>{{ cart.shippingName }}</b></p>
                <p v-if="cart.shippingCompany">{{ cart.shippingCompany }}</p>
                <p v-if="cart.shippingFirstLine">{{ cart.shippingFirstLine }}</p>
                <p v-if="cart.shippingSecondLine">{{ cart.shippingSecondLine }}</p>
                <p v-if="cart.shippingThirdLine">{{ cart.shippingThirdLine }}</p>
                <p v-if="cart.shippingRegion">{{ cart.shippingRegion }}</p>
                <p v-if="cart.shippingEmail">{{ cart.shippingEmail }}</p>
                <p v-if="cart.shippingPhone">{{ cart.shippingPhone }}</p>
              </address>
              <UiButtonGroup>
                <SfButton :href="$route('web.cart.shipping.address.edit')" small>
                  <template #before>
                    <SfIconPen aria-hidden="true" />
                  </template>
                  <template #default>
                    {{ $t('SHOP.ACTION.EDIT') }}
                  </template>
                </SfButton>
              </UiButtonGroup>
            </template>
          </CartStage>
          <CartStage id="cs-shipping" :current="stage" :stage="4" :title="$t('SHOP.CART.HEADER.SHIPPING_METHOD')">
            <template #default>
              <slot />
            </template>
            <template #summary>
              <div>
                <p v-if="cart.shippingOptionName"><b>{{ cart.shippingOptionName }}</b></p>
                <p v-if="cart.shippingPointName">{{ cart.shippingPointName }}</p>
              </div>
              <UiButtonGroup>
                <SfButton :href="$route('web.cart.shipping.option.edit')" small>
                  <template #before>
                    <SfIconPen aria-hidden="true" />
                  </template>
                  <template #default>
                    {{ $t('SHOP.ACTION.EDIT') }}
                  </template>
                </SfButton>
              </UiButtonGroup>
            </template>
          </CartStage>
          <CartStage id="cs-invoice" :current="stage" :stage="5" :title="$t('SHOP.CART.HEADER.BILLING_ADDRESS')">
            <template #default>
              <slot />
            </template>
            <template #summary>
              <address>
                <p v-if="cart.billingName"><b>{{ cart.billingName }}</b></p>
                <p v-if="cart.billingCompany"><b>{{ cart.billingCompany }}</b></p>
                <p v-if="cart.billingFirstLine">{{ cart.billingFirstLine }}</p>
                <p v-if="cart.billingSecondLine">{{ cart.billingSecondLine }}</p>
                <p v-if="cart.billingThirdLine">{{ cart.billingThirdLine }}</p>
                <p v-if="cart.billingRegion">{{ cart.billingRegion }}</p>
                <p v-if="cart.billingEmail">{{ cart.billingEmail }}</p>
                <p v-if="cart.billingPhone">{{ cart.billingPhone }}</p>
                <p v-if="cart.billingVatId"><b>{{ $t('SHOP.CART.LABEL.VAT_ID') }} {{ cart.billingVatId }}</b></p>
              </address>
              <UiButtonGroup>
                <SfButton :href="$route('web.cart.billing.address.edit')" small>
                  <template #before>
                    <SfIconPen aria-hidden="true" />
                  </template>
                  <template #default>
                    {{ $t('SHOP.ACTION.EDIT') }}
                  </template>
                </SfButton>
              </UiButtonGroup>
            </template>
          </CartStage>
          <CartStage id="cs-payment" :current="stage" :stage="6" :title="$t('SHOP.CART.HEADER.BILLING_METHOD')">
            <template #default>
              <slot />
            </template>
            <template #summary>
              <div>
                <p v-if="cart.billingMethodOptionName"><b>{{ cart.billingMethodOptionName }}</b></p>
              </div>
              <UiButtonGroup>
                <SfButton :href="$route('web.cart.billing.option.edit')" small>
                  <template #before>
                    <SfIconPen aria-hidden="true" />
                  </template>
                  <template #default>
                    {{ $t('SHOP.ACTION.EDIT') }}
                  </template>
                </SfButton>
              </UiButtonGroup>
            </template>
          </CartStage>
          <CartStage id="cs-information" :current="stage" :stage="7" :title="$t('SHOP.CART.HEADER.ADDITIONAL_INFO')">
            <template #default>
              <slot />
            </template>
            <template #summary>
              <div>
                <p><i>{{ cart.hasCustomerNote ? cart.customerNote : $t('SHOP.CART.MESSAGE.NO_ADDITIONAL_INFO') }}</i></p>
              </div>
              <UiButtonGroup>
                <SfButton :href="$route('web.cart.note.edit')" small>
                  <template #before>
                    <SfIconPen aria-hidden="true" />
                  </template>
                  <template #default>
                    {{ $t('SHOP.ACTION.EDIT') }}
                  </template>
                </SfButton>
              </UiButtonGroup>
            </template>
          </CartStage>
          <CartStage id="cs-conditions" :current="stage" :stage="8" :title="$t('SHOP.CART.HEADER.CONDITIONS')">
            <template #default>
              <slot />
            </template>
          </CartStage>
          <CartStage id="cs-summary" :current="stage" :stage="9" :title="$t('SHOP.CART.HEADER.SUMMARY')">
            <template #default>
              <slot />
            </template>
          </CartStage>
        </div>
        <div class="order-details__column order-details__column--right">
          <SfTile class="order-details__summary" number>
            <template #outer>
              <h2 class="order-heading">
                {{ $t('SHOP.CART.HEADER.CART') }}
              </h2>
            </template>
            <div class="order-details__products">
              <OrderSummaryItems v-for="item in cart.items" :key="item.id" :item="item" />
            </div>
            <div class="order-details__options-summary">
              <OrderSummaryShippingOption class="order-details__option-summary" />
              <OrderSummaryBillingOption class="order-details__option-summary" />
              <OrderSummaryRealizationTime class="order-details__option-summary" />
            </div>
            <OrderSummary :order="cart" />
          </SfTile>
        </div>
      </div>
    </div>
  </main>
</template>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";


  .order-details {
    display: grid;
    grid-template-columns: 2fr minmax(450px, 1fr);
    gap: 3rem;

    &__section-header {
      display: flex;
      align-items: center;
      column-gap: 0.625rem;
    }

    &__number {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      height: 2.5rem;
      width: 2.5rem;
      font-size: 1.5rem;
      font-weight: var(--font-weight-bold);
      line-height: 1;
      padding: 0.375rem;
      border-radius: 50%;
      border-style: var(--sf-order-details-number-border-style);
      border-color: var(--sf-order-details-number-border-color);
      border-width: var(--sf-order-details-number-border-width);
      counter-increment: list;

      &::after {
        content: counter(list)
      }
    }

    &__column--left {
      display: flex;
      flex-direction: column;
      counter-reset: list;
    }

    &__summary {
      --sf-tile-gap: 2rem;
      position: sticky;
      top: var(--sf-order-cart-summary-sticky-top);
    }

    &__products {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__options-summary {
      display: flex;
      flex-direction: column;
      border-radius: var(--sf-order-details-options-summary-border-radius);
      background-color: var(--sf-order-details-options-summary-bg);
    }

    &__option-summary {
      &:first-child {
        padding-top: 1rem;
      }

      &:last-child {
        padding-bottom: 1rem;
      }
    }
  }

  @include media.query(xl) {
    .order-details {
      grid-template-columns: 1fr;
      margin: 0;
      gap: 1rem;

      &__column--right {
        margin-top: 1rem;
        position: static;
      }

      &__heading {
        margin-top: 20px;
      }
    }
  }

  @include media.query(lg) {
    .order-details {
      margin: 0;
      gap: 10px;

      .order-heading {
        font-size: 1.25rem;
      }

      &__tile {
        padding-top: 1rem;
        padding-bottom: 1rem;
      }

      &__number {
        height: 2rem;
        width: 2rem;
        font-size: 1.25rem;
      }

      &__heading {
        font-size: 25px;
        margin-top: 0;
        padding-left: 10px;
      }

      &__summary {
        gap: 1rem;
        padding-top: 1rem;
        padding-bottom: 1rem;
      }
    }
  }
</style>
